import { computed, effect, inject, untracked } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { PlatformStore } from '../../platform';
import { RouterStore } from '../../router';
import { ThemeStore } from '../../theme';
import { isCollapsedSidebarRoute } from '../layout-config';

type LayoutState = {
  sideMenuOpened: boolean;
  staticMenuDesktopInactive: boolean;
  overlayMenuActive: boolean;
  mobileMenuActive: boolean;
  menuHoverActive: boolean;
  configSidebarVisible: boolean;
};

const initialState: LayoutState = {
  sideMenuOpened: false,
  staticMenuDesktopInactive: false,
  overlayMenuActive: false,
  mobileMenuActive: false,
  menuHoverActive: false,
  configSidebarVisible: false,
};

/**
 * Signal store managing layout-related UI state.
 *
 * Handles side menu visibility, overlay/static menu modes, and reacts to
 * {@link PlatformStore} size changes and {@link ThemeStore} menu mode.
 *
 * @example
 * import { inject } from '@angular/core';
 * import { LayoutStore } from '@indutrust/shared-data';
 *
 * export class ExampleComponent {
 *   readonly layout = inject(LayoutStore);
 *
 *   collapse() {
 *     this.layout.closeSideMenu();
 *   }
 * }
 */
export const LayoutStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => {
    // ThemeStore injected at the compute level for reactive menuMode
    const themeStore = inject(ThemeStore);

    return {
      isSidebarActive: computed(
        () => store.overlayMenuActive() || store.mobileMenuActive()
      ),
      isOverlay: computed(() => themeStore.menuMode() === 'overlay'),
    };
  }),
  withMethods((store) => {
    const themeStore = inject(ThemeStore);
    const platformStore = inject(PlatformStore);

    return {
      openSideMenu() {
        if (store.sideMenuOpened()) {
          return;
        }
        patchState(store, { sideMenuOpened: true });
      },
      closeSideMenu() {
        if (!store.sideMenuOpened()) {
          return;
        }
        patchState(store, { sideMenuOpened: false });
      },
      toggleSideMenu() {
        patchState(store, {
          sideMenuOpened: !store.sideMenuOpened(),
        });
      },

      /**
       * Sakai-style menu toggle that respects overlay vs static mode.
       */
      onMenuToggle() {
        if (themeStore.menuMode() === 'overlay') {
          patchState(store, {
            overlayMenuActive: !store.overlayMenuActive(),
          });
        }

        if (platformStore.isSmallScreen()) {
          patchState(store, {
            mobileMenuActive: !store.mobileMenuActive(),
          });
        } else {
          patchState(store, {
            staticMenuDesktopInactive: !store.staticMenuDesktopInactive(),
          });
        }
      },

      showConfigSidebar() {
        patchState(store, {
          configSidebarVisible: true,
        });
      },
      hideConfigSidebar() {
        patchState(store, {
          configSidebarVisible: false,
        });
      },

      onRouteChange() {
        if (
          !store.overlayMenuActive() &&
          !store.mobileMenuActive() &&
          !store.menuHoverActive()
        ) {
          return;
        }
        patchState(store, {
          overlayMenuActive: false,
          mobileMenuActive: false,
          menuHoverActive: false,
        });
      },
    };
  }),
  withHooks((store) => {
    const platformStore = inject(PlatformStore);
    const routerStore = inject(RouterStore);

    return {
      onInit() {
        let previousUrlForDesktopSidebar: string | null = null;

        /**
         * Syncs the slide-out side menu with viewport: closed on small screens, open on desktop.
         */
        effect(() => {
          if (platformStore.isSmallScreen()) {
            if (store.sideMenuOpened()) {
              store.closeSideMenu();
            }
          } else if (!store.sideMenuOpened()) {
            store.openSideMenu();
          }
        });

        /**
         * On each navigation: clears overlay / mobile / hover menu state.
         * On mobile, also closes the slide-out side menu.
         * Uses {@link untracked} so {@link PlatformStore} reads are not tracked as effect deps.
         */
        effect(() => {
          // Dependency: re-run on navigation (value unused here).
          routerStore.url();

          untracked(() => {
            store.onRouteChange();
            if (platformStore.isSmallScreen()) {
              store.closeSideMenu();
            }
          });
        });

        /**
         * Desktop only: auto-collapses the static sidebar on full-bleed and collapsed-sidebar
         * routes; expands it again when navigating from those routes to a normal page.
         * Uses {@link untracked} so platform checks are not tracked as effect deps.
         */
        effect(() => {
          const url = routerStore.url();

          untracked(() => {
            const wasCollapsedSidebarRoute =
              typeof previousUrlForDesktopSidebar === 'string' &&
              isCollapsedSidebarRoute(previousUrlForDesktopSidebar);

            const shouldCollapseDesktopSidebar = isCollapsedSidebarRoute(url);

            if (
              shouldCollapseDesktopSidebar &&
              !platformStore.isSmallScreen() &&
              !store.staticMenuDesktopInactive()
            ) {
              patchState(store, {
                staticMenuDesktopInactive: true,
              });
            } else if (
              wasCollapsedSidebarRoute &&
              !shouldCollapseDesktopSidebar &&
              !platformStore.isSmallScreen() &&
              store.staticMenuDesktopInactive()
            ) {
              patchState(store, {
                staticMenuDesktopInactive: false,
              });
            }

            previousUrlForDesktopSidebar = url;
          });
        });
      },
    };
  })
);
